/**
 * Estado de paginación del hilo de mensajes, por conversación.
 *
 * POR QUÉ UN STORE LATERAL Y NO EL QUERY CACHE
 * --------------------------------------------
 * El cache `['messages', <id>]` TIENE que seguir siendo un `Message[]` plano.
 * Hay ~15 escritores quirúrgicos sobre esa clave (realtime-manager: upsert,
 * ticks de entrega, media_ready, transcripción, borrado; queries.ts: envío
 * optimista, edición, transcripción, request-contact-info) y TODOS guardan con
 * la forma `(prev) => Array.isArray(prev) ? ... : prev`.
 *
 * Eso quiere decir que si el cache pasara a la forma `{pages: [...]}` de un
 * infinite query, esos escritores no fallarían: **no-opearían en silencio**. Y
 * dos de ellos —los ticks de entrega (`inbox:message_status`) y la
 * transcripción de audio— no tienen `invalidateQueries` de respaldo, así que
 * el estado se perdería para siempre sin un error, sin un log y sin un test
 * rojo. Un cambio de forma acá se paga en corrupción silenciosa.
 *
 * Así que la forma del cache no se toca y lo que la paginación necesita saber
 * —cuántas páginas pidió el agente, qué dijo el último `meta`, si hay un fetch
 * en vuelo, si el último falló— vive acá.
 *
 * El `queryFn` HIDRATA este store como efecto lateral del fetch, que es el
 * mismo patrón que ya usan `agents()` (hidrata presencia) y
 * `seedPresenceSnapshots` en este mismo módulo.
 *
 * Ver `message-pagination.ts` para el contrato del endpoint.
 */
import { create } from 'zustand';
import type { MessagesMeta } from './message-pagination';
import type { LocalizedError } from '@/lib/i18n/errors';

/**
 * Página de 50 — el default del backend. No hay tope del lado del servidor
 * (gotcha 6 del contrato), pero pedir de a 50 mantiene acotado el costo de
 * cada request y del re-camino en cada refetch.
 */
export const MESSAGES_PAGE_SIZE = 50;

/**
 * Techo del re-camino de páginas. 40 páginas = 2.000 mensajes: más que la
 * conversación más larga observada en producción (353) con margen de años de
 * `single_conversation_lock`.
 *
 * Existe para que un refetch no dispare una cadena de requests sin fondo, no
 * para recortar historia. Cuando se toca, se avisa (`onCapReached`) en vez de
 * truncar en silencio: un tope silencioso se lee como "esto es todo", que es
 * exactamente el bug que este contrato viene a matar.
 */
export const MAX_HISTORY_PAGES = 40;

const META_DESCONOCIDO: MessagesMeta = {
  total: null,
  hasMore: false,
  nextCursor: null,
  present: false,
};

export interface ThreadHistory {
  /**
   * Cuántas páginas debe reproducir el `queryFn` en el próximo fetch completo.
   * Arranca en 1 y sube con cada "cargar anteriores". Es lo que hace que un
   * refetch por WS reconstruya la MISMA ventana que el agente estaba mirando
   * en vez de colapsarla a los últimos 50.
   */
  pages: number;
  /** Lo que dijo el `meta` de la página más vieja de la ventana cargada. */
  meta: MessagesMeta;
  /** Hay un "cargar anteriores" en vuelo. */
  loading: boolean;
  /**
   * Mensaje del último fallo de paginación, o `null`. Se muestra en la cabeza
   * del hilo: un fallo al cargar historia no puede quedar mudo, porque desde
   * afuera se ve idéntico a "no hay más".
   */
  error: LocalizedError | string | null;
}

/** Entrada por defecto. Constante de módulo: se comparte por referencia para
 *  que los selectores de zustand no re-rendericen en cada llamada. */
export const THREAD_HISTORY_INICIAL: ThreadHistory = {
  pages: 1,
  meta: META_DESCONOCIDO,
  loading: false,
  error: null,
};

interface MessageHistoryState {
  byConversation: Record<string, ThreadHistory>;
  /** Lectura sin hook, para el `queryFn` y otros caminos fuera de React. */
  get(conversationId: string): ThreadHistory;
  /**
   * Hidratación desde la respuesta del server (efecto lateral del `queryFn`).
   * `pagesLoaded` es cuántas páginas se recorrieron DE VERDAD: si el hilo se
   * agotó antes de llegar a `pages`, se baja el contador para que el próximo
   * refetch no vuelva a intentar páginas que ya sabemos vacías.
   *
   * NO toca `loading` ni `error`: esos ejes son del "cargar anteriores" que
   * los prendió. Un refetch de fondo que termina en el medio de un pedido del
   * agente no tiene por qué apagarle el spinner ni borrarle el error.
   */
  hydrate(conversationId: string, meta: MessagesMeta, pagesLoaded: number): void;
  /** Sube el techo de la ventana en una página. Devuelve el nuevo total. */
  wantMore(conversationId: string): number;
  /**
   * Deshace un `wantMore` fallido, pero SÓLO si nadie más movió el techo
   * mientras tanto. Un rollback absoluto pisaría la ventana que un walk
   * concurrente ya amplió y la dejaría más chica que el cache.
   */
  rollbackPages(conversationId: string, esperado: number): void;
  setLoading(conversationId: string, loading: boolean): void;
  setError(conversationId: string, error: ThreadHistory['error']): void;
  /** Vuelve una conversación al estado inicial (reintento manual, 403, purga). */
  reset(conversationId: string): void;
  /** Tira todo (logout). */
  clear(): void;
}

function patch(
  state: MessageHistoryState,
  conversationId: string,
  cambios: Partial<ThreadHistory>,
): Pick<MessageHistoryState, 'byConversation'> {
  const actual = state.byConversation[conversationId] ?? THREAD_HISTORY_INICIAL;
  return {
    byConversation: {
      ...state.byConversation,
      [conversationId]: { ...actual, ...cambios },
    },
  };
}

export const useMessageHistoryStore = create<MessageHistoryState>((set, get) => ({
  byConversation: {},

  get(conversationId) {
    return get().byConversation[conversationId] ?? THREAD_HISTORY_INICIAL;
  },

  hydrate(conversationId, meta, pagesLoaded) {
    const actual = get().get(conversationId);
    set((state) =>
      patch(state, conversationId, {
        meta,
        // Un `meta` AUSENTE es "no sé", no "no hay más", así que no puede
        // ACHICAR la ventana. Pasa de verdad durante un deploy parcial: si
        // media flota contesta con `meta` y la otra media sin él, cada refetch
        // que cae en una instancia vieja cortaría el recorrido en la primera
        // página y le encogería el mundo al agente. Conservando el techo, el
        // siguiente recorrido que sí reciba `meta` reproduce la misma ventana.
        pages: meta.present
          ? Math.max(1, Math.min(pagesLoaded, MAX_HISTORY_PAGES))
          : actual.pages,
      }),
    );
  },

  wantMore(conversationId) {
    const siguiente = Math.min(get().get(conversationId).pages + 1, MAX_HISTORY_PAGES);
    set((state) => patch(state, conversationId, { pages: siguiente }));
    return siguiente;
  },

  rollbackPages(conversationId, esperado) {
    if (get().get(conversationId).pages !== esperado) return;
    set((state) => patch(state, conversationId, { pages: Math.max(1, esperado - 1) }));
  },

  setLoading(conversationId, loading) {
    set((state) => patch(state, conversationId, { loading }));
  },

  setError(conversationId, error) {
    set((state) => patch(state, conversationId, { error }));
  },

  reset(conversationId) {
    set((state) => {
      if (!(conversationId in state.byConversation)) return state;
      const siguiente = { ...state.byConversation };
      delete siguiente[conversationId];
      return { byConversation: siguiente };
    });
  },

  clear() {
    set({ byConversation: {} });
  },
}));

/** Hook de lectura. Devuelve una referencia estable cuando no hay entrada. */
export function useThreadHistory(conversationId: string): ThreadHistory {
  return useMessageHistoryStore(
    (state) => state.byConversation[conversationId] ?? THREAD_HISTORY_INICIAL,
  );
}
